import { Provider, signal } from '@angular/core';

import { ATTENTION_PROJECTIONS, AttentionProjection } from '@shared/services/attention/attention-projections';

import { SIGNAGE_RAIL } from './signage.routes';

export { SIGNAGE_RAIL } from './signage.routes';

/* What the signage store last counted, published here so the ROOT projection reads it without
   importing the store — which would pull signage into the app's initial chunk (the 7 MB budget; the
   gym's and the hotel's pattern). Until signage has been opened this session there is nothing
   counted, and no badge. Nothing here may import the store or the records (`signage.routes.ts` is
   root-light too). */
const OFFLINE = signal(0);
const STALE = signal(0);
const OPEN = signal(0);

/** What the store counted — the DS's three rules (D374). */
export interface SignageBadgeCounts {
  /** Screens with no heartbeat past the tolerance — Screens. */
  readonly offline: number;
  /** Screens playing an older version than the one published — Screens. */
  readonly stale: number;
  /** 1 while a takeover with NO end time is live — Schedule. */
  readonly takeoverOpen: number;
}

/** The store's one write. */
export function publishSignageBadges(c: SignageBadgeCounts): void {
  if (OFFLINE() !== c.offline) OFFLINE.set(c.offline);
  if (STALE() !== c.stale) STALE.set(c.stale);
  if (OPEN() !== c.takeoverOpen) OPEN.set(c.takeoverOpen);
}

/**
 * Signage's three attention rules (D374): `player_offline`, `stale_cache` and `takeover_open`. Each is
 * its own rule — its own Setup toggle — and each is SILENT when nothing is wrong. Offline and stale
 * sum on Screens at the loudest tone; the open-ended takeover badges Schedule, behind `takeover`, so a
 * tier or role without it never evaluates the rule.
 */
export function signageAttentionProjections(): readonly AttentionProjection[] {
  return [
    {
      /* A screen that stopped reporting. Warning, not danger: it goes on playing its cache — nothing
         is black — but nothing new reaches it and nobody can see what it is showing. */
      rule: { id: 'signage.screens.offline', moduleId: 'signage', capKey: null, railItemId: SIGNAGE_RAIL.screens, enabled: true, scope: 'site' },
      count: OFFLINE.asReadonly(),
      tone: signal('warning' as const).asReadonly(),
    },
    {
      /* A screen still playing an older version than the one published — relative to a published
         version, never to the clock (D117). */
      rule: { id: 'signage.screens.stale', moduleId: 'signage', capKey: null, railItemId: SIGNAGE_RAIL.screens, enabled: true, scope: 'site' },
      count: STALE.asReadonly(),
      tone: signal('warning' as const).asReadonly(),
    },
    {
      /* An override with NO end time: the one still up on Monday because everyone assumed somebody
         else would end it. A takeover with an `until` ends itself and is not attention. */
      rule: { id: 'signage.takeover.open', moduleId: 'signage', capKey: 'takeover', railItemId: SIGNAGE_RAIL.schedule, enabled: true, scope: 'site' },
      count: OPEN.asReadonly(),
      tone: signal('warning' as const).asReadonly(),
    },
  ];
}

/** Register signage's projections at the root (AppModule), so the shell can show them. */
export function provideSignageAttention(): Provider[] {
  return [{ provide: ATTENTION_PROJECTIONS, useValue: signageAttentionProjections, multi: true }];
}
